import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  CheckCircle2,
  CircleDollarSign,
  Clock,
  CreditCard,
  Database,
  Gauge,
  KeyRound,
  ListChecks,
  Plus,
  RefreshCcw,
  ReceiptText,
  Save,
  Server,
  Settings,
  ShieldCheck,
  WalletCards,
  Zap,
} from "lucide-react";
import { groupDashboardAccounts, STATUS_LABELS, STATUS_ORDER } from "../shared/balances.js";
import "./styles.css";

const currency = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const statusIcons = {
  critical: AlertTriangle,
  sync_error: AlertTriangle,
  reserve: WalletCards,
  request_balance: CircleDollarSign,
  watch: Clock,
  card_watch: CreditCard,
  unknown: Database,
  ok: CheckCircle2,
};

const statusTitles = {
  critical: "Ação agora",
  sync_error: "Erro de sync",
  reserve: "Em reserva",
  request_balance: "Solicitar saldo",
  watch: "Acompanhar",
  card_watch: "Cartão/pós-pago",
  unknown: "Sem dados",
  ok: "Ok",
};

const statusTone = {
  critical: "danger",
  sync_error: "danger",
  reserve: "warning",
  request_balance: "warning",
  watch: "info",
  card_watch: "info",
  unknown: "muted",
  ok: "success",
};

const accountTypeLabels = {
  meta_prepaid: "Meta pré-paga",
  meta_card: "Meta cartão",
  meta_postpaid: "Meta pós-paga",
  google_estimated: "Google estimado",
  google_invoice: "Google invoice",
  unknown: "Sem tipo",
};

const tabItems = [
  ["dashboard", "Painel", Gauge],
  ["accounts", "Contas", ListChecks],
  ["credentials", "Credenciais", KeyRound],
  ["topups", "Recargas", ReceiptText],
  ["history", "Sync", Server],
];

const LOCAL_API_BASE =
  window.__AD_BALANCES_LOCAL_API_BASE ||
  import.meta.env.VITE_AD_BALANCES_LOCAL_API_BASE ||
  "http://127.0.0.1:5173";

function resolveApiUrl(path) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  return new URL(path, LOCAL_API_BASE).toString();
}

async function api(path, init = {}) {
  const url = resolveApiUrl(path);
  const fetchInit = {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  };

  if (url.startsWith("http://127.0.0.1") || url.startsWith("http://localhost")) {
    fetchInit.targetAddressSpace = "loopback";
  }

  const response = await fetch(url, fetchInit);
  const payload = await response.json();
  if (!response.ok || payload.ok === false) {
    throw new Error(payload?.error?.message || "Falha na API local");
  }
  return payload.data;
}

function money(value) {
  return value == null || Number.isNaN(Number(value)) ? "—" : currency.format(Number(value));
}

function days(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(1).replace(".", ",")} d`;
}

function percent(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Math.round(Number(value) * 100)}%`;
}

function lastSeen(value) {
  if (!value) {
    return "Sem consulta";
  }
  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function compactAccountMeta(account) {
  return `${account.platform} · ${accountTypeLabels[account.accountType] || account.accountType} · ${
    account.providerAccountId || "sem id"
  }`;
}

function reserveRule(account) {
  return `${money(account.reserveDailySpend)}/dia · ${account.reserveDays || 0} dias`;
}

function spendStatus(account) {
  if (account.spendRatio == null) {
    return "Sem previsão";
  }
  if (account.overPlanned) {
    return "Acima";
  }
  if (account.underPlanned) {
    return "Baixo";
  }
  return "Dentro";
}

function spendTone(account) {
  if (account.overPlanned) {
    return "acima";
  }
  if (account.underPlanned) {
    return "baixo";
  }
  return account.spendRatio == null ? "sem-previsao" : "dentro";
}

function ActionCard({ account, onRequestBalance, onSync }) {
  const Icon = statusIcons[account.status] || Database;
  return (
    <article className={`priority-card status-${account.status}`}>
      <div className="priority-card-header">
        <span className={`status-badge tone-${statusTone[account.status] || "muted"}`}>
          <Icon size={15} />
          {account.statusLabel}
        </span>
        <span className="platform-badge">{account.platform}</span>
      </div>
      <div className="priority-title">
        <div>
          <strong>{account.displayName}</strong>
          <span>{accountTypeLabels[account.accountType] || account.accountType}</span>
        </div>
      </div>
      <dl className="priority-metrics">
        <div>
          <dt>Saldo</dt>
          <dd>{money(account.availableBalance)}</dd>
        </div>
        <div>
          <dt>Reserva</dt>
          <dd>{money(account.reserveAmount)}</dd>
        </div>
        <div>
          <dt>Até reserva</dt>
          <dd>{days(account.daysUntilReserve)}</dd>
        </div>
        <div>
          <dt>Hoje</dt>
          <dd>{money(account.latestSnapshot?.spendToday)}</dd>
        </div>
        <div>
          <dt>Dia normal</dt>
          <dd>{money(account.plannedDailySpend)}</dd>
        </div>
        <div>
          <dt>Dia reserva</dt>
          <dd>{money(account.reserveDailySpend)}</dd>
        </div>
      </dl>
      <div className="spend-track" aria-label="Gasto realizado versus previsto">
        <span style={{ width: `${Math.min(100, Math.max(0, Number(account.spendRatio || 0) * 100))}%` }} />
      </div>
      <div className="priority-footer">
        <span>{percent(account.spendRatio)} do previsto diário</span>
        <div className="row-actions">
          <button type="button" className="icon-button" onClick={() => onSync(account)} title="Sincronizar">
            <RefreshCcw size={17} />
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={() => onRequestBalance(account)}
            title="Marcar saldo solicitado"
          >
            <CircleDollarSign size={17} />
          </button>
        </div>
      </div>
    </article>
  );
}

function AccountTable({ accounts, onRequestBalance, onSync, onEdit }) {
  return (
    <div className="table-scroll">
      <table className="accounts-table">
        <thead>
          <tr>
            <th>Conta</th>
            <th>Situação</th>
            <th>Saldo atual</th>
            <th>Reserva operacional</th>
            <th>Gasto diário</th>
            <th>Gasto na reserva</th>
            <th>Hoje</th>
            <th>Até reserva</th>
            <th>Última consulta</th>
            <th aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          {accounts.map((account) => {
            const Icon = statusIcons[account.status] || Database;
            return (
              <tr key={account.id} className={`status-${account.status}`}>
                <td>
                  <div className="table-account">
                    <strong>{account.displayName}</strong>
                    <span>{compactAccountMeta(account)}</span>
                  </div>
                </td>
                <td>
                  <span className={`status-badge tone-${statusTone[account.status] || "muted"}`}>
                    <Icon size={15} />
                    {account.statusLabel}
                  </span>
                </td>
                <td>{money(account.availableBalance)}</td>
                <td>
                  <strong>{money(account.reserveAmount)}</strong>
                  <span className="muted-cell">{reserveRule(account)}</span>
                </td>
                <td>{money(account.plannedDailySpend)}</td>
                <td>{money(account.reserveDailySpend)}</td>
                <td>
                  <strong>{money(account.latestSnapshot?.spendToday)}</strong>
                  <span className={`muted-cell spend-${spendTone(account)}`}>
                    {percent(account.spendRatio)} · {spendStatus(account)}
                  </span>
                </td>
                <td>{days(account.daysUntilReserve)}</td>
                <td>{lastSeen(account.latestSnapshot?.snapshotAt)}</td>
                <td>
                  <div className="row-actions">
                    {onEdit ? (
                      <button
                        type="button"
                        className="icon-button subtle"
                        onClick={() => onEdit(account)}
                        title="Editar regras"
                      >
                        <Settings size={17} />
                      </button>
                    ) : null}
                    <button type="button" className="icon-button" onClick={() => onSync(account)} title="Sincronizar">
                      <RefreshCcw size={17} />
                    </button>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => onRequestBalance(account)}
                      title="Marcar saldo solicitado"
                    >
                      <CircleDollarSign size={17} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard({ dashboard, onRefresh, onRequestBalance, onSync }) {
  const groups = useMemo(() => groupDashboardAccounts(dashboard?.accounts || []), [dashboard]);
  const accounts = dashboard?.accounts || [];
  const sortedAccounts = useMemo(
    () =>
      [...accounts].sort(
        (left, right) => STATUS_ORDER.indexOf(left.status) - STATUS_ORDER.indexOf(right.status)
      ),
    [accounts]
  );
  const actionAccounts = useMemo(
    () =>
      ["critical", "sync_error", "reserve", "request_balance"]
        .flatMap((status) => groups[status] || [])
        .slice(0, 6),
    [groups]
  );
  const urgentCount =
    (dashboard?.totals?.critical || 0) +
    (dashboard?.totals?.sync_error || 0) +
    (dashboard?.totals?.reserve || 0) +
    (dashboard?.totals?.request_balance || 0);
  const plannedDaily = accounts.reduce((total, account) => total + Number(account.plannedDailySpend || 0), 0);
  const spendToday = accounts.reduce(
    (total, account) => total + Number(account.latestSnapshot?.spendToday || 0),
    0
  );
  const closestReserve = accounts
    .map((account) => account.daysUntilReserve)
    .filter((value) => value != null && Number.isFinite(Number(value)))
    .sort((a, b) => Number(a) - Number(b))[0];

  return (
    <section className="dashboard-shell">
      <header className="page-header">
        <div>
          <p className="eyebrow">ad-balances.copyfirst.studio</p>
          <h1>Ad Balances</h1>
          <div className="header-meta">
            <span className="api-chip">
              <Server size={14} />
              {LOCAL_API_BASE}
            </span>
            <span className="api-chip">
              <ShieldCheck size={14} />
              {dashboard?.mode || "local"}
            </span>
          </div>
        </div>
        <div className="header-actions">
          <button type="button" className="primary-button" onClick={onRefresh}>
            <RefreshCcw size={17} />
            Atualizar
          </button>
        </div>
      </header>

      <section className="metric-grid compact">
        <Metric icon={AlertTriangle} label="Atenção" value={urgentCount} detail="contas em fila" tone="danger" />
        <Metric icon={CircleDollarSign} label="Gasto hoje" value={money(spendToday)} detail={`${percent(spendToday / Math.max(1, plannedDaily))} do previsto`} tone="info" />
        <Metric icon={WalletCards} label="Reserva mais próxima" value={days(closestReserve)} detail={`${dashboard?.totals?.request_balance || 0} solicitando saldo`} tone="warning" />
        <Metric icon={CheckCircle2} label="Operando" value={dashboard?.totals?.ok || 0} detail={`${accounts.length} contas no total`} tone="success" />
      </section>

      <section className="panel table-panel front-table-panel">
        <div className="panel-title table-title">
          <div className="title-left">
            <ListChecks size={18} />
            <div>
              <h2>Situação de todas as contas</h2>
              <span>{accounts.length} contas monitoradas · gasto normal e gasto de reserva visíveis</span>
            </div>
          </div>
          <div className="status-rail inline">
            {STATUS_ORDER.map((status) => {
              const count = groups[status]?.length || 0;
              const Icon = statusIcons[status] || Database;
              return (
                <span className={`status-pill tone-${statusTone[status] || "muted"}`} key={status}>
                  <Icon size={15} />
                  {statusTitles[status] || STATUS_LABELS[status]}
                  <strong>{count}</strong>
                </span>
              );
            })}
          </div>
        </div>
        <AccountTable accounts={sortedAccounts} onRequestBalance={onRequestBalance} onSync={onSync} />
      </section>

      <section className="panel action-panel compact-actions">
        <div className="panel-title compact-title">
          <AlertTriangle size={18} />
          <div>
            <h2>Fila operacional</h2>
            <span>{actionAccounts.length ? `${actionAccounts.length} prioridades agora` : "Sem pendências"}</span>
          </div>
        </div>
        <div className="priority-list horizontal">
          {actionAccounts.length ? (
            actionAccounts.map((account) => (
              <ActionCard
                key={account.id}
                account={account}
                onRequestBalance={onRequestBalance}
                onSync={onSync}
              />
            ))
          ) : (
            <div className="empty-state compact-empty">
              <CheckCircle2 size={22} />
              <strong>Nenhuma ação agora</strong>
            </div>
          )}
        </div>
      </section>
    </section>
  );
}

function Metric({ icon: Icon, label, value, detail, tone }) {
  return (
    <div className={`metric tone-${tone}`}>
      <div className="metric-icon">
        <Icon size={18} />
      </div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function Accounts({ accounts, credentials, onSaved, onRequestBalance, onSync }) {
  const [form, setForm] = useState(defaultAccountForm());
  const [editingId, setEditingId] = useState(null);

  function edit(account) {
    setEditingId(account.id);
    setForm({
      platform: account.platform,
      accountType: account.accountType,
      providerAccountId: account.providerAccountId,
      displayName: account.displayName,
      credentialId: account.credentialId || "",
      plannedDailySpend: account.plannedDailySpend,
      reserveDailySpend: account.reserveDailySpend,
      reserveDays: account.reserveDays,
      alertBeforeReserveDays: account.alertBeforeReserveDays,
      paymentStatus: account.paymentStatus || "",
      notes: account.notes || "",
    });
  }

  async function submit(event) {
    event.preventDefault();
    const method = editingId ? "PATCH" : "POST";
    const path = editingId ? `/api/accounts/${editingId}` : "/api/accounts";
    await api(path, {
      method,
      body: JSON.stringify(normalizeNumberFields(form)),
    });
    setEditingId(null);
    setForm(defaultAccountForm());
    await onSaved();
  }

  return (
    <section className="accounts-admin">
      <form className="panel" onSubmit={submit}>
        <div className="panel-title">
          <Settings size={18} />
          <div>
            <h2>{editingId ? "Editar regras da conta" : "Nova conta"}</h2>
            <span>Gasto normal, gasto na reserva e aviso operacional</span>
          </div>
        </div>
        <Field label="Nome" value={form.displayName} onChange={(displayName) => setForm({ ...form, displayName })} />
        <div className="field-grid">
          <Select label="Plataforma" value={form.platform} onChange={(platform) => setForm({ ...form, platform })}>
            <option value="meta">Meta</option>
            <option value="google">Google</option>
          </Select>
          <Select
            label="Tipo"
            value={form.accountType}
            onChange={(accountType) => setForm({ ...form, accountType })}
          >
            <option value="meta_prepaid">Meta pré-paga</option>
            <option value="meta_card">Meta cartão</option>
            <option value="meta_postpaid">Meta pós-paga</option>
            <option value="google_estimated">Google estimado</option>
            <option value="google_invoice">Google invoice</option>
            <option value="unknown">Desconhecida</option>
          </Select>
        </div>
        <Field
          label="ID da conta"
          value={form.providerAccountId}
          onChange={(providerAccountId) => setForm({ ...form, providerAccountId })}
        />
        <Select
          label="Credencial"
          value={form.credentialId}
          onChange={(credentialId) => setForm({ ...form, credentialId })}
        >
          <option value="">Sem credencial</option>
          {credentials.map((credential) => (
            <option key={credential.id} value={credential.id}>
              {credential.provider} · {credential.name}
            </option>
          ))}
        </Select>
        <div className="field-grid">
          <Field
            label="Gasto diário planejado"
            type="number"
            value={form.plannedDailySpend}
            onChange={(plannedDailySpend) => setForm({ ...form, plannedDailySpend })}
          />
          <Field
            label="Gasto diário na reserva"
            type="number"
            value={form.reserveDailySpend}
            onChange={(reserveDailySpend) => setForm({ ...form, reserveDailySpend })}
          />
          <Field
            label="Reserva em dias"
            type="number"
            value={form.reserveDays}
            onChange={(reserveDays) => setForm({ ...form, reserveDays })}
          />
          <Field
            label="Avisar antes da reserva"
            type="number"
            value={form.alertBeforeReserveDays}
            onChange={(alertBeforeReserveDays) => setForm({ ...form, alertBeforeReserveDays })}
          />
        </div>
        <Field
          label="Status pagamento"
          value={form.paymentStatus}
          onChange={(paymentStatus) => setForm({ ...form, paymentStatus })}
        />
        <button type="submit" className="primary-button">
          <Save size={17} />
          Salvar
        </button>
      </form>

      <section className="panel table-panel">
        <div className="panel-title table-title">
          <div className="title-left">
            <Database size={18} />
            <div>
              <h2>Todas as contas</h2>
              <span>Situação atual, regras de gasto e ações rápidas</span>
            </div>
          </div>
        </div>
        <AccountTable
          accounts={accounts}
          onRequestBalance={onRequestBalance}
          onSync={onSync}
          onEdit={edit}
        />
      </section>
    </section>
  );
}

function Credentials({ credentials, onSaved, onTest }) {
  const [form, setForm] = useState({ provider: "meta", name: "", secret: "" });
  return (
    <section className="split-layout">
      <form
        className="panel"
        onSubmit={async (event) => {
          event.preventDefault();
          await api("/api/credentials", { method: "POST", body: JSON.stringify(form) });
          setForm({ provider: "meta", name: "", secret: "" });
          await onSaved();
        }}
      >
        <div className="panel-title">
          <KeyRound size={18} />
          <h2>Credencial</h2>
        </div>
        <Select label="Provider" value={form.provider} onChange={(provider) => setForm({ ...form, provider })}>
          <option value="meta">Meta</option>
          <option value="google">Google</option>
        </Select>
        <Field label="Nome" value={form.name} onChange={(name) => setForm({ ...form, name })} />
        <Field
          label="Token/chave"
          type="password"
          value={form.secret}
          onChange={(secret) => setForm({ ...form, secret })}
        />
        <button type="submit" className="primary-button">
          <Plus size={17} />
          Cadastrar
        </button>
      </form>

      <section className="panel table-panel">
        <div className="panel-title">
          <KeyRound size={18} />
          <h2>Credenciais</h2>
        </div>
        <div className="table-list">
          {credentials.map((credential) => (
            <div className="list-item as-row" key={credential.id}>
              <div>
                <strong>{credential.name}</strong>
                <span>{credential.provider} · {credential.status} · {credential.hasSecret ? "com segredo" : "sem segredo"}</span>
              </div>
              <button type="button" className="icon-button" onClick={() => onTest(credential)} title="Testar">
                <Zap size={17} />
              </button>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}

function Topups({ accounts, topups, onSaved }) {
  const googleAccounts = accounts.filter((account) => account.platform === "google");
  const [form, setForm] = useState({
    accountId: googleAccounts[0]?.id || "",
    amount: "",
    occurredOn: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  useEffect(() => {
    if (!form.accountId && googleAccounts[0]?.id) {
      setForm((current) => ({ ...current, accountId: googleAccounts[0].id }));
    }
  }, [form.accountId, googleAccounts]);

  return (
    <section className="split-layout">
      <form
        className="panel"
        onSubmit={async (event) => {
          event.preventDefault();
          await api("/api/topups", { method: "POST", body: JSON.stringify(normalizeNumberFields(form)) });
          setForm({ ...form, amount: "", notes: "" });
          await onSaved();
        }}
      >
        <div className="panel-title">
          <CircleDollarSign size={18} />
          <h2>Recarga Google</h2>
        </div>
        <Select label="Conta" value={form.accountId} onChange={(accountId) => setForm({ ...form, accountId })}>
          {googleAccounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.displayName}
            </option>
          ))}
        </Select>
        <div className="field-grid">
          <Field label="Valor" type="number" value={form.amount} onChange={(amount) => setForm({ ...form, amount })} />
          <Field
            label="Data"
            type="date"
            value={form.occurredOn}
            onChange={(occurredOn) => setForm({ ...form, occurredOn })}
          />
        </div>
        <Field label="Notas" value={form.notes} onChange={(notes) => setForm({ ...form, notes })} />
        <button type="submit" className="primary-button">
          <Plus size={17} />
          Lançar
        </button>
      </form>

      <section className="panel table-panel">
        <div className="panel-title">
          <WalletCards size={18} />
          <h2>Histórico</h2>
        </div>
        <div className="table-list">
          {topups.map((topup) => {
            const account = accounts.find((item) => item.id === topup.accountId);
            return (
              <div className="list-item" key={topup.id}>
                <strong>{money(topup.amount)} · {topup.occurredOn}</strong>
                <span>{account?.displayName || topup.accountId}</span>
              </div>
            );
          })}
        </div>
      </section>
    </section>
  );
}

function History({ syncRuns }) {
  return (
    <section className="panel">
      <div className="panel-title">
        <Clock size={18} />
        <h2>Sync runs</h2>
      </div>
      <div className="table-list">
        {syncRuns.map((run) => (
          <div className="list-item" key={run.id}>
            <strong>{run.runType} · {run.status}</strong>
            <span>{run.startedAt ? new Date(run.startedAt).toLocaleString("pt-BR") : "sem data"}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type={type} value={value ?? ""} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function Select({ label, value, onChange, children }) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value ?? ""} onChange={(event) => onChange(event.target.value)}>
        {children}
      </select>
    </label>
  );
}

function defaultAccountForm() {
  return {
    platform: "meta",
    accountType: "meta_prepaid",
    providerAccountId: "",
    displayName: "",
    credentialId: "",
    plannedDailySpend: 20,
    reserveDailySpend: 10,
    reserveDays: 7,
    alertBeforeReserveDays: 5,
    paymentStatus: "",
    notes: "",
  };
}

function normalizeNumberFields(input) {
  return {
    ...input,
    plannedDailySpend: input.plannedDailySpend === "" ? 0 : Number(input.plannedDailySpend),
    reserveDailySpend: input.reserveDailySpend === "" ? 0 : Number(input.reserveDailySpend),
    reserveDays: input.reserveDays === "" ? 0 : Number(input.reserveDays),
    alertBeforeReserveDays: input.alertBeforeReserveDays === "" ? 5 : Number(input.alertBeforeReserveDays),
    amount: input.amount === "" || input.amount == null ? undefined : Number(input.amount),
  };
}

function App() {
  const [tab, setTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [topups, setTopups] = useState([]);
  const [syncRuns, setSyncRuns] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setError(null);
    setLoading(true);
    try {
      const [dashboardData, accountData, credentialData, topupData, syncData] = await Promise.all([
        api("/api/dashboard"),
        api("/api/accounts"),
        api("/api/credentials"),
        api("/api/topups"),
        api("/api/sync-runs"),
      ]);
      setDashboard(dashboardData);
      setAccounts(accountData);
      setCredentials(credentialData);
      setTopups(topupData);
      setSyncRuns(syncData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function requestBalance(account) {
    await api("/api/balance-requests", {
      method: "POST",
      body: JSON.stringify({ accountId: account.id, notes: `Solicitado por ${account.statusLabel}` }),
    });
    await refresh();
  }

  async function syncAccount(account) {
    await api(`/api/accounts/${account.id}/sync`, { method: "POST", body: JSON.stringify({}) });
    await refresh();
  }

  async function testCredential(credential) {
    await api(`/api/credentials/${credential.id}/test`, { method: "POST", body: JSON.stringify({}) });
    await refresh();
  }

  return (
    <main>
      <nav className="top-nav">
        <div className="brand">
          <CircleDollarSign size={22} />
          <span>Ad Balances</span>
        </div>
        <div className="tabs">
          {tabItems.map(([key, label, TabIcon]) => (
            <button key={key} type="button" className={tab === key ? "active" : ""} onClick={() => setTab(key)}>
              <TabIcon size={15} />
              {label}
            </button>
          ))}
        </div>
      </nav>

      {error ? <div className="error-bar">{error}</div> : null}
      {loading && !dashboard ? <div className="loading">Carregando</div> : null}

      {tab === "dashboard" && dashboard ? (
        <Dashboard
          dashboard={dashboard}
          onRefresh={refresh}
          onRequestBalance={requestBalance}
          onSync={syncAccount}
        />
      ) : null}
      {tab === "accounts" ? (
        <Accounts
          accounts={accounts}
          credentials={credentials}
          onSaved={refresh}
          onRequestBalance={requestBalance}
          onSync={syncAccount}
        />
      ) : null}
      {tab === "credentials" ? (
        <Credentials credentials={credentials} onSaved={refresh} onTest={testCredential} />
      ) : null}
      {tab === "topups" ? <Topups accounts={accounts} topups={topups} onSaved={refresh} /> : null}
      {tab === "history" ? <History syncRuns={syncRuns} /> : null}
    </main>
  );
}

createRoot(document.getElementById("root")).render(<App />);
